/**
 * Cross-frame bridge for embedded tours.
 *
 * The envelope deliberately mirrors Storylane's shape — a `message`
 * discriminator wrapping a `payload` with `demo` / `step` / `flow` — so any GTM
 * tag, Zapier recipe or analytics snippet already written against Storylane
 * works here with a one-word change. That compatibility is nearly free and is
 * worth more than a tidier bespoke schema.
 *
 * `targetOrigin` is never `'*'`. Demo URLs carry the workbook id and
 * `lead_identify` concerns a real person, so an unrecognised parent gets
 * nothing at all rather than a wildcard broadcast.
 */

export const TOUR_EVENT_MESSAGE = 'fusegrid-tour-event'

export type TourBridgeEvent =
  | 'demo_open'
  | 'page_view'
  | 'flow_start'
  | 'step_view'
  | 'flow_end'
  | 'demo_finished'
  | 'lead_identify'
  | 'open_external_url'
  | 'checklist_item_view'
  | 'primary_cta'
  | 'secondary_cta'
  | 'convert_cta'

export type TourBridgePayload = {
  event: TourBridgeEvent
  demo: { id: string; name: string; url: string }
  step?: { id: string; index: number }
  flow?: { id: string; name: string }
  /** Identification signal only — never the address itself. */
  lead?: { identified: true }
  target?: { url: string }
  checklist_item?: { id: string }
  cta?: { label: string }
}

export type TourBridge = {
  post: (payload: TourBridgePayload) => void
  enabled: boolean
}

/**
 * Build a bridge for the given parent origin. When the origin is absent (not
 * embedded, or an origin outside the allowlist) every post is a no-op — the
 * caller does not branch.
 */
export function createTourBridge(embedOrigin: string | null): TourBridge {
  const enabled = Boolean(embedOrigin) && typeof window !== 'undefined' && window.parent !== window
  return {
    enabled,
    post(payload: TourBridgePayload) {
      if (!enabled || !embedOrigin) return
      try {
        window.parent.postMessage({ message: TOUR_EVENT_MESSAGE, payload }, embedOrigin)
      } catch {
        /* a cross-origin parent that refuses the post is not the tour's problem */
      }
    },
  }
}
